import React, { useRef, useState } from 'react'
import CarLogo from './CarLogo'
import Icon from './Icon'
import { useOutsideClick } from '../tools/Events'

import { useSelector } from 'react-redux'
import { getFastestPerCar } from '../redux/data/selectors' 
//import { getYou } from './redux/options/selectors' 

/**
 * Farthest a click may be from a point to show its tooltip, in pixels of the chart; the points are
 * small, so a click near one is enough
 * @type {number}
 */
const HIT_DISTANCE = 12

/**
 * Get the point of a chart nearest to a click, within the hit distance and inside the plot
 * @param {Object} chart drawn Google chart
 * @param {Object} data its data table
 * @param {number} x horizontal place of the click in the chart
 * @param {number} y vertical place of the click in the chart
 * @param {Function} getRow gets the lap of a cell from its row and column, null for no point
 * @returns {{ row: Object, x: number, y: number, cell: Object }|null} lap, place and cell of the point
 */
const getNearestPoint = ( chart, data, x, y, getRow ) =>
{
  const layout = chart.getChartLayoutInterface()
  const plot = layout.getChartAreaBoundingBox()
  let nearest = null
  let best = HIT_DISTANCE

  for ( let row = 0; row < data.getNumberOfRows(); row++ )
  {
    const px = layout.getXLocation( data.getValue( row, 0 ) )
    if ( px < plot.left || px > plot.left + plot.width || Math.abs( px - x ) > best ) continue

    for ( let column = 1; column < data.getNumberOfColumns(); column++ )
    {
      const value = data.getColumnType( column ) === 'number' ? data.getValue( row, column ) : null
      const lap = value === null ? null : getRow( row, column )
      if ( !lap ) continue

      const py = layout.getYLocation( value )
      const distance = Math.hypot( px - x, py - y )
      if ( distance > best ) continue

      best = distance
      nearest = { row: lap, x: px, y: py, cell: { row, column } }
    }
  }

  return nearest
}

/**
 * Show the tooltip of a clicked point of a chart, the same in every chart: a bubble below the point
 * with its arrow pointing at it. A click near a point is enough, as the points are small. Put ref
 * and onClick on an element around the chart, with position: relative, render tooltip in it and
 * add onChartReady( getRow ) as the ready event of the chart; close closes it, such as when the chart
 * is zoomed or panned. The click is handled here rather than by the chart, whose wrapper passes on
 * no clicks
 * @returns {{ ref: Object, onClick: Function, onChartReady: Function, close: Function, tooltip: JSX.Element|null }} tooltip parts
 */
export const useChartTooltip = () =>
{
  const [ tip, setTip ] = useState( null )
  const ref = useOutsideClick( () => setTip( null ) )
  // the drawn chart and how to get the lap of its cells
  const chartRef = useRef( null )

  /**
   * Get the ready event of a chart, which keeps the drawn chart for the clicks
   * @param {Function} getRow gets the lap of a cell from its row and column, null for no point
   * @returns {Function} event callback
   */
  const onChartReady = ( getRow ) => ( { chartWrapper } ) =>
  {
    chartRef.current = { chartWrapper, getRow }
  }

  /**
   * Show the lap of the point nearest to the click, or close the tooltip when there is none, such
   * as for a click on the buttons to pan and zoom
   * @param {MouseEvent} event click
   * @returns {void}
   */
  const onClick = ( event ) =>
  {
    const { chartWrapper = null, getRow = null } = chartRef.current || {}
    const chart = chartWrapper ? chartWrapper.getChart() : null
    const container = chartWrapper ? document.getElementById( chartWrapper.getContainerId() ) : null
    if ( !ref.current || !chart || !container ) return setTip( null )

    // from the screen to the size of the chart, which a zoom such as on the kiosk screen scales
    const area = ref.current.getBoundingClientRect()
    const box = container.getBoundingClientRect()
    const scale = ref.current.offsetWidth ? area.width / ref.current.offsetWidth : 1
    const x = ( event.clientX - box.left ) / scale
    const y = ( event.clientY - box.top ) / scale
    const nearest = getNearestPoint( chart, chartWrapper.getDataTable(), x, y, getRow )

    // the point marked as selected, as the chart itself would for a click right on it
    chart.setSelection( nearest ? [ nearest.cell ] : [] )
    if ( !nearest ) return setTip( null )

    setTip( {
      row: nearest.row,
      x: ( box.left - area.left ) / scale + nearest.x,
      y: ( box.top - area.top ) / scale + nearest.y + POINT_RADIUS
    } )
  }

  /**
   * Close the tooltip
   * @returns {void}
   */
  const close = () => setTip( null )

  const tooltip = tip ? <ChartTooltip row={ tip.row } x={ tip.x } y={ tip.y }/> : null
  return { ref, onClick, onChartReady, close, tooltip }
}

/**
 * Room below the middle of a point to its edge, where the arrow points
 * @type {number}
 */
const POINT_RADIUS = 4

const ChartTooltip = ( props ) =>
{
  let {
    x = 0,
    y = 0,
    className = '',
    row = null
  } = props

  // user car number
  //const you = useSelector( getYou )
  const you = '1306'
  // get fastest time per car, sorted on laptime
  const fastest = useSelector( getFastestPerCar )
  // extract row ids
  const fastest_ids = fastest.map( f => f.id )  

  if ( !row ) return null

  const style = {
    left: x + 'px',
    top: y + 'px',
  }

  className += ' chart-tooltip'

  if ( String( row.car ) === String( you ) )
  {
    // your car
    className += ' you'
  }

  if ( row.penalty > 0 || row.comment !== '')
  {
    // orange for invalid laps
    className += ' invalid'
  }  
  else if ( fastest_ids.indexOf( row.id ) === 0 )
  {
    // purple for fastest lap
    className += ' fastest'
  }
  else if ( fastest_ids.indexOf( row.id ) > 0 )
  {
    // green for fast laps
    className += ' fast'
  }

  return (
    <div className={ className } style={ style }>
      <div className="car"><CarLogo brand={ row.model } />{ row.car }</div>
      <div className="time"><Icon icon="stopwatch"/>{ row.laptime }</div>
  </div>
  )
}

export default ChartTooltip